const SVG_NS = 'http://www.w3.org/2000/svg';

/**
 * Chuỗi SVG của lớp DIM để ghép vào ảnh: bỏ các phần chỉ dành cho thao tác
 * (vùng bắt chuột trong suốt, marker snap — đánh dấu data-export="skip"), đặt
 * kích thước theo khung nhìn để co giãn đúng khi vẽ lên ảnh lớn hơn.
 *
 * @param {SVGSVGElement} svg - lớp DIM đang hiển thị
 * @param {{width: number, height: number}} size - kích thước khung nhìn (px CSS)
 */
export const serializeOverlaySvg = (svg, { width, height }) => {
  const clone = svg.cloneNode(true);
  clone.querySelectorAll('[data-export="skip"]').forEach(n => n.remove());
  // Con trỏ / nền hover chỉ có ý nghĩa khi tương tác
  clone.querySelectorAll('[style]').forEach(n => {
    n.setAttribute(
      'style',
      n
        .getAttribute('style')
        .split(';')
        .filter(rule => !/^\s*(cursor|user-select)\s*:/.test(rule))
        .join(';')
    );
  });
  clone.removeAttribute('id');
  clone.setAttribute('xmlns', SVG_NS);
  clone.setAttribute('width', String(width));
  clone.setAttribute('height', String(height));
  clone.setAttribute('viewBox', `0 0 ${width} ${height}`);
  // Nền trong suốt, không dính vị trí tuyệt đối của trang
  clone.setAttribute('style', 'background: none');
  return new XMLSerializer().serializeToString(clone);
};

const loadSvgImage = svgText =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Không dựng được ảnh từ lớp DIM'));
    img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svgText)}`;
  });

/**
 * Ghép ảnh canvas 3D với lớp SVG DIM thành một ảnh.
 * Giá trị đo trong SVG đã được tính từ hình học (không từ pixel), ảnh chỉ ghép
 * hai lớp lại với nhau.
 *
 * @param {{canvas: HTMLCanvasElement, svg?: SVGSVGElement|null,
 *          scale?: number, format?: 'image/png'|'image/jpeg', quality?: number,
 *          background?: string, includeDimensions?: boolean}} opts
 * @returns {Promise<Blob>}
 */
export const composeViewWithDimensions = async ({
  canvas,
  svg = null,
  scale = 2,
  format = 'image/png',
  quality = 0.92,
  background = '#ffffff',
  includeDimensions = true,
}) => {
  if (!canvas) throw new Error('Không có canvas 3D để xuất ảnh');
  const width = canvas.clientWidth || canvas.width;
  const height = canvas.clientHeight || canvas.height;
  const outW = Math.max(1, Math.round(width * scale));
  const outH = Math.max(1, Math.round(height * scale));

  const out = document.createElement('canvas');
  out.width = outW;
  out.height = outH;
  const ctx = out.getContext('2d');
  // JPEG không có kênh trong suốt: tô nền trước
  ctx.fillStyle = background;
  ctx.fillRect(0, 0, outW, outH);
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(canvas, 0, 0, outW, outH);

  if (includeDimensions && svg) {
    const image = await loadSvgImage(
      serializeOverlaySvg(svg, { width, height })
    );
    ctx.drawImage(image, 0, 0, outW, outH);
  }

  return new Promise((resolve, reject) => {
    out.toBlob(
      blob =>
        blob && blob.size > 0 ? resolve(blob) : reject(new Error('Ảnh rỗng')),
      format,
      quality
    );
  });
};

/** Tên file xuất: dim-view-YYYYMMDD-HHmmss.png */
export const exportFileName = (format = 'image/png', date = new Date()) => {
  const p = n => String(n).padStart(2, '0');
  const stamp = `${date.getFullYear()}${p(date.getMonth() + 1)}${p(date.getDate())}-${p(date.getHours())}${p(date.getMinutes())}${p(date.getSeconds())}`;
  return `dim-view-${stamp}.${format === 'image/jpeg' ? 'jpg' : 'png'}`;
};

export const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};
